import * as React from 'react';
import {
    Breakpoints,
    TokenTextAppearance,
    styled
} from '@volkswagen-onehub/components-core';

import {AuthoringWrapper} from '../../../components/AuthoringWrapper';
import {AlertBox} from '../../../components/AlertBox';
import {CopyItem} from '../../editorial/items/CopyItem';
import {ButtonElement} from '../../editorial/elements/buttonElement/ButtonElement';
import {HeadingElement} from '../../editorial/elements/HeadingElement';
import {MediaElement} from '../../editorial/elements/MediaElement';
import {
    SlideContent,
    StyledButtonWrapper
} from '../../../d6/components/stage-slider/components/SlideContent';
import {UniversalDisclaimerContainer} from '../../../components/disclaimers/UniversalDisclaimerContainer';

import {C} from '../../../registries/compatibilty';
import {ContextTrackingData} from '../../../context/tracking/TrackingService';
import {GeneralDisclaimerProvider} from '../../../context/disclaimer/GeneralDisclaimerProvider';
import {MapTo} from '../../../infrastructure/compatibility/MapTo';
import {MediaContext} from '../../../context/media/MediaContext';
import {ResponsiveMediaRendererConf} from '../../../components/ResponsiveMediaRenderer';
import {TrackingActionOverride} from '../../../infrastructure/TrackableLinkProps';
import {useAuthorHeightCalc} from '../../../utils/useAuthorHeightCalc';
import {useTrackingService} from '../../../context';
import {XfStageSlideModel} from '../../../../generated/core';
import {useMediaQueries} from '../../../d6/hooks';

const StyledStageSliderDisclaimerWrapper = styled.div`
    display: flex;
    ${({
        theme: {
            size: {dynamic0200, static250}
        }
    }) => `padding: ${static250} ${dynamic0200}`}
`;

// Note: max-width values are roughly determined by size of the media's gradient
const StyledTextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.size.static100};

    @media (min-width: ${Breakpoints.b560}px) {
        max-width: ${props => props.theme.size.grid016};
    }

    @media (min-width: ${Breakpoints.b960}px) {
        max-width: ${props => props.theme.size.grid010};
    }
`;

export interface ExtraXfStageSlideProps {
    clearDisclaimerHeights: () => void;
    itemCount: number;
    itemPosition: number;
    reportDisclaimerHeight: (payload: number) => void;
}

type InternalXfStageSlideProps = XfStageSlideModel & ExtraXfStageSlideProps;

const InternalXfStageSlide = ({
    contentId,
    cqItems,
    itemCount,
    itemPosition,
    clearDisclaimerHeights,
    reportDisclaimerHeight,
    showPrimaryButton,
    showSecondaryButton,
    validationError,
    validationMessages
}: InternalXfStageSlideProps) => {
    const trackingService = useTrackingService();
    const authorSlideHeight = useAuthorHeightCalc(0.8);

    const isInEditor = C.isInEditor();
    const hasValue = !Boolean(cqItems?.copy?.empty);
    const isMobile = useMediaQueries()?.isMobile;
    const showCopy = C.isInEditor() || (!isMobile && hasValue);

    const responsiveSlideMediaConfig: ResponsiveMediaRendererConf[] = [
        {
            mediaQuery: '(max-aspect-ratio: 1/1)',
            sizes: '100vw',
            aspectRatio: 'r_4_5',
            portray: true
        },
        {
            mediaQuery: '(min-aspect-ratio: 1/1) and (max-aspect-ratio: 4/3)',
            sizes: '100vw',
            aspectRatio: 'r_4_3'
        },
        {
            mediaQuery: '(min-aspect-ratio: 4/3)',
            sizes: '100vw',
            aspectRatio: 'r_16_9'
        }
    ];

    const trackingActionOverride: TrackingActionOverride = (
        href: string,
        linkName: string,
        contextData: ContextTrackingData
    ) =>
        trackingService.trackButtonClick(contentId, href, linkName, {
            ...contextData,
            itemCount,
            itemPosition
        });

    const alertBox = isInEditor && validationError && (
        <AlertBox>
            {validationMessages.map((msg, idx) => (
                <span key={idx}>{msg}</span>
            ))}
        </AlertBox>
    );

    const buttons = showPrimaryButton && (
        <StyledButtonWrapper>
            {showSecondaryButton && (
                <ButtonElement
                    path="secondaryButton"
                    trackingActionOverride={trackingActionOverride}
                />
            )}
            <ButtonElement
                path="primaryButton"
                trackingActionOverride={trackingActionOverride}
            />
        </StyledButtonWrapper>
    );

    const copyItem = showCopy && (
        <CopyItem
            path="copy"
            hideItemDisclaimers
            appearance={TokenTextAppearance.bigcopy100}
        />
    );

    const disclaimerContainer = (
        <UniversalDisclaimerContainer
            customStylingWrapper={StyledStageSliderDisclaimerWrapper}
            displayTypes={['T3_SECTION_BASED', 'T4_ITEM_BASED']}
        />
    );

    const heading = (
        <HeadingElement
            path="heading"
            hideItemDisclaimers
            style="H1"
            order="H1"
        />
    );
    const mediaElement = (
        <MediaContext.Provider value={{ratio: 'r_1_1'}}>
            <MediaElement
                path="media"
                allowNestedDisclaimers
                containerProps={{
                    matchParent: true
                }}
                responsiveMediaConfig={responsiveSlideMediaConfig}
                useParentAspectRatio
            />
        </MediaContext.Provider>
    );

    return (
        <AuthoringWrapper title="Stage Slide XF">
            {alertBox}
            <GeneralDisclaimerProvider
                displayTypes={['T3_SECTION_BASED', 'T4_ITEM_BASED']}
            >
                <SlideContent
                    clearDisclaimerHeights={clearDisclaimerHeights}
                    disclaimers={disclaimerContainer}
                    heightOverride={authorSlideHeight}
                    mediaElement={mediaElement}
                    reportDisclaimerHeight={reportDisclaimerHeight}
                >
                    <StyledTextWrapper>
                        {heading}
                        {copyItem}
                    </StyledTextWrapper>
                    {buttons}
                </SlideContent>
            </GeneralDisclaimerProvider>
        </AuthoringWrapper>
    );
};

export const XfStageSlide = MapTo<ExtraXfStageSlideProps>(
    'vwa-ngw18/components/structure/xfStageSlide',
    InternalXfStageSlide,
    {noElementRecursively: true}
);
